import { useMemo, useState } from 'react';

import { Modal, Tooltip, Typography } from 'antd';

import { BADGE_SIZES } from './constants';

import useImage from '@shared/hooks/useImage';

import Utils from '@/Utils';
import cn from 'classnames';
import PropTypes from 'prop-types';

import css from './style.module.scss';

const { Text } = Typography;

const BadgeElement = ({
  size = BADGE_SIZES.DEFAULT,
  data,
  className = '',
  classNames = {
    image: '',
    name: '',
    root: className,
  },
  disabled = false,
  hasPreview = false,
  hideName = false,
  tooltipKey = 'description',
  maxNameLength = 0, // При 0 текст не обрезается
  hasTooltip = true,
}) => {
  const [open, setOpen] = useState(false);
  const image = useImage(data?.imageUuid, 'badge');

  const changePreviewModal = () => setOpen(!open);

  const previewModal = useMemo(() => {
    if (!hasPreview) return <></>;

    return (
      <Modal title={data?.name} open={open} onCancel={changePreviewModal} footer={null} width={350}>
        <div className={css['BadgeElement-modal']}>
          <img className={css['BadgeElement-image']} src={image} alt='' />
          <Text>{data?.description}</Text>
        </div>
      </Modal>
    );
  }, [data, open, hasPreview]);

  return (
    <div
      className={cn(
        css['BadgeElement'],
        {
          [css['BadgeElement-preview']]: hasPreview,
        },
        classNames?.root
      )}
      onClick={changePreviewModal}
    >
      <Tooltip title={hasTooltip && data?.[tooltipKey]}>
        <img
          className={cn(
            css['BadgeElement-image'],
            {
              [css['BadgeElement-image-mini']]: size === BADGE_SIZES.MINI,
              [css['BadgeElement-image-small']]: size === BADGE_SIZES.SMALL,
              [css['BadgeElement-image-middle']]: size === BADGE_SIZES.DEFAULT,
              [css['BadgeElement-image-large']]: size === BADGE_SIZES.LARGE,
              [css['BadgeElement-image-xlarge']]: size === BADGE_SIZES.XLARGE,
              [css['BadgeElement-image-disabled']]: disabled,
            },
            classNames?.image
          )}
          src={image}
          alt=''
        />
      </Tooltip>
      {!hideName && (
        <Text
          className={cn(
            css['BadgeElement-name'],
            {
              [css['BadgeElement-name-disabled']]: disabled,
            },
            classNames?.name
          )}
        >
          {Utils.renderWithTooltip(data?.name, maxNameLength)}
        </Text>
      )}
      {previewModal}
    </div>
  );
};

BadgeElement.propTypes = {
  size: PropTypes.string, // Принимает 'mini' || 'small' || 'middle' || 'large' || 'xlarge'
  data: PropTypes.shape({
    // Данные о награде
    description: PropTypes.string,
    id: PropTypes.number,
    imageUuid: PropTypes.string,
    name: PropTypes.string,
    status: PropTypes.string,
  }),
  hideName: PropTypes.bool, // Скрывает название награды
  className: PropTypes.string, // Доп. классы для корневого элемента
  disabled: PropTypes.bool, // Превращает награду в серую показывая, что она не получена
  hasPreview: PropTypes.bool, // Предпросмотр награды
  tooltipKey: PropTypes.string, // Изменить ключ для tooltip ибо иногда требуется отображать не name, а description
  maxNameLength: PropTypes.number, // Максимальная длина названия награды
  hasTooltip: PropTypes.bool, // Отображение tooltip
  classNames: PropTypes.shape({
    // Точечные классы для стилизации конкретного элемента
    root: PropTypes.string,
    image: PropTypes.string,
    name: PropTypes.string,
  }),
};

export default BadgeElement;
